'use client'

import { useSearchParams } from 'next/navigation'
import { useCallback, useMemo } from 'react'

import type { TaskListTypeFilter } from '@/features/tasks/api/watchTaskApi'
import { useFanTasksAccess } from '@/features/tasks/hooks/useFanTasksAccess'
import { useTaskTakeGate } from '@/features/tasks/providers/TaskTakeGateProvider'
import { useIsHydrated } from '@/lib/datetime/useIsHydrated'
import { useRestoreTasksListScroll } from '@/lib/tasks/tasksListReturn'
import {
  parseTasksTabParam,
  TASKS_LIST_DEFAULT_STATE,
  TASKS_TAB_QUERY_KEY,
  type TasksListState,
  type TasksMyStatus,
  type TasksView
} from '@/lib/tasks/tasksTabs'

import type { TaskFeedPlatform } from '../TaskFeedCard/taskFeedCard.constants'
import TasksExploreFeed from '../TasksExploreFeed'
import type { TasksFeedSortId } from '../TasksFeedNav/tasksFeedNav.constants'
import TasksMyFeed from '../TasksMyFeed'
import S from './TasksListingSection.module.scss'
import {
  parseTasksListFilters,
  type TasksListFilters,
  tasksListUrl
} from './tasksListUrl'

/** What a feed reads and changes; the chip is the type (Explore) or status (My). */
export type TasksFeedControls<C extends string> = {
  onViewChange: (view: TasksView) => void
  chip: C
  onChipChange: (chip: C) => void
  platforms: readonly TaskFeedPlatform[]
  onPlatformsChange: (platforms: readonly TaskFeedPlatform[]) => void
  sort: TasksFeedSortId
  onSortChange: (sort: TasksFeedSortId) => void
  /** Resets the chip and every filter; the order stays. */
  onClearAll: () => void
}

export type TasksMyFeedControls = TasksFeedControls<TasksMyStatus> & {
  types: readonly TaskListTypeFilter[]
  onTypesChange: (types: readonly TaskListTypeFilter[]) => void
}

const NONE: readonly never[] = []

const DEFAULT_FILTERS: TasksListFilters = {
  segment: 'all',
  platforms: NONE,
  types: NONE,
  sort: 'default'
}

/**
 * Writes the view and filters into the URL, so Back from a task and the
 * language switch (LangSelector copies the query) land on the same list.
 * Reads the live URL, not render state: handlers change several at once.
 */
function updateListUrl(
  patch: Partial<TasksListFilters>,
  state?: TasksListState
) {
  const params = new URLSearchParams(window.location.search)
  const next = tasksListUrl(
    state ?? parseTasksTabParam(params.get(TASKS_TAB_QUERY_KEY)),
    { ...parseTasksListFilters(params), ...patch }
  )
  // The native History API syncs useSearchParams without a server round trip.
  window.history.replaceState(null, '', next)
}

const TasksListingSection = () => {
  const searchParams = useSearchParams()
  const { canParticipateInFanTasks } = useFanTasksAccess()
  const { guardTaskTake } = useTaskTakeGate()
  const isHydrated = useIsHydrated()
  useRestoreTasksListScroll()

  const listState = useMemo(
    () => parseTasksTabParam(searchParams.get(TASKS_TAB_QUERY_KEY)),
    [searchParams]
  )
  const { segment, platforms, types, sort } = useMemo(
    () => parseTasksListFilters(searchParams),
    [searchParams]
  )

  const applyListState = useCallback((next: TasksListState) => {
    updateListUrl({}, next)
  }, [])
  const setPlatforms = useCallback(
    (next: readonly TaskFeedPlatform[]) => updateListUrl({ platforms: next }),
    []
  )
  const setTypes = useCallback(
    (next: readonly TaskListTypeFilter[]) => updateListUrl({ types: next }),
    []
  )
  const setSort = useCallback(
    (next: TasksFeedSortId) => updateListUrl({ sort: next }),
    []
  )

  // My tasks needs a fan session; guests and clubs see Explore. The session
  // is only known on the client, so the server render keeps the URL view.
  const visibleState =
    isHydrated && listState.view === 'my' && !canParticipateInFanTasks
      ? TASKS_LIST_DEFAULT_STATE
      : listState

  const handleViewChange = useCallback(
    (view: TasksView) => {
      if (view === visibleState.view) {
        return
      }
      if (view === 'my' && !canParticipateInFanTasks) {
        // Opens the sign-up / club-only dialog instead of switching.
        guardTaskTake(() => {})
        return
      }
      // Each view has its own platforms and orders: start from scratch.
      updateListUrl(DEFAULT_FILTERS, { ...TASKS_LIST_DEFAULT_STATE, view })
    },
    [canParticipateInFanTasks, guardTaskTake, visibleState.view]
  )

  const shared = {
    onViewChange: handleViewChange,
    platforms,
    onPlatformsChange: setPlatforms,
    sort,
    onSortChange: setSort
  }

  return (
    <div className={S.root}>
      {visibleState.view === 'explore' ? (
        <TasksExploreFeed
          controls={{
            ...shared,
            chip: segment,
            onChipChange: next => updateListUrl({ segment: next }),
            onClearAll: () => {
              updateListUrl({ segment: 'all', platforms: NONE })
            }
          }}
        />
      ) : (
        <TasksMyFeed
          controls={{
            ...shared,
            chip: visibleState.myStatus,
            onChipChange: myStatus => {
              applyListState({ view: 'my', myStatus })
            },
            types,
            onTypesChange: setTypes,
            onClearAll: () => {
              updateListUrl(
                { platforms: NONE, types: NONE },
                { view: 'my', myStatus: 'all' }
              )
            }
          }}
        />
      )}
    </div>
  )
}

export default TasksListingSection
